import styled from '@emotion/styled'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { getToBePaidData } from '../../../pure-js/libs/EnrollmentHelper'
import {
  Activity,
  EnrollmentPaymentStatus,
  PayFor,
  PayForEnum,
  PaymentMethod
} from '../../../pure-js/types/GrooverTypes'
import QRCodeElement from '../components/QRCodeElement'
import { BorderRadixes } from '../enums/BorderRadixes'
import { EIGHT_PX, FOUR_PX, SIXTEEN_PX, THIRTY_TWO_PX, TWELVE_PX, TWO_PX, TWENTY_FOUR_PX } from '../enums/Spacings'
import { State } from '../hooks/useAppState'
import { useClient } from '../hooks/useClient'
import useIsMobile from '../hooks/useIsMobile'
import { useRegistrationQrCodes } from '../hooks/useRegistrationQrCodes'
import { useCurrency } from '../hooks/useCurrency'
import { isGetObfuscatedEnrollmentResponse, useEnrollment } from '../libs/ReactQueryHooks'
import { formatGrooverText, getGrooverText } from '../libs/GrooverTextRepository'
import { _getLanguageCode, _getDefaultLanguageCode } from '../hooks/useLanguage'
import { getFunctionUrl } from '../libs/FunctionsUrlHelper'
import ActivityDetails from './ActivityDetails'
import swedishAppStoreBadge from '/Download_on_the_App_Store_Badge_SE_RGB_blk_100317.svg'
import englishAppStoreBadge from '/Download_on_the_App_Store_Badge_US-UK_RGB_blk_092917.svg'
import appleWalletImage from '/apple-wallet-image.svg'
import googleWalletImage from '/google-wallet-badge.svg'
import Box from './Box'
import CourseRegistrationReceiptWrapper from './CourseRegistrationReceiptWrapper'
import GrooverText from './GrooverText'
import RegisterOpenInCheckinAppButton from './RegisterOpenInCheckinAppButton'
import { useIsSuperAdmin } from '../hooks/useIsSuperAdmin'
import iconCompleted from '/public/iconCompleted.svg'

type Props = {
  state: State
  activity: Activity
  onSetMainUserSsn: (mainUserSsn: string) => void
  onSetPartnerUserSsn: (partnerUserSsn: string) => void
}

const CourseRegistrationStepFiveOpenedContent = ({ state, activity, onSetMainUserSsn, onSetPartnerUserSsn }: Props) => {
  const isMobile = useIsMobile()
  const client = useClient(activity)
  const currency = useCurrency(client || undefined)
  const [expandedActivityId, setExpandedActivityId] = useState<string | null>(null)
  const enrollmentUserIdFromUrl = useMemo(
    () => new URLSearchParams(window.location.search).get('u')?.trim() || undefined,
    []
  )
  const { data: obfuscatedEnrollmentResponse } = useEnrollment(state, enrollmentUserIdFromUrl)
  const enrollment = isGetObfuscatedEnrollmentResponse(obfuscatedEnrollmentResponse)
    ? obfuscatedEnrollmentResponse.enrollment
    : undefined
  const { approvedActivities, allQRCodes, currentQRData } = useRegistrationQrCodes({
    state,
    enrollmentUserId: enrollmentUserIdFromUrl
  })
  const isSuperAdmin = useIsSuperAdmin()
  // Localhost DEV: always show for staff testing. Prod: SuperAdmin Firebase claims on this origin.
  const showOpenInCheckinApp = (isSuperAdmin || import.meta.env.DEV) && !!client?.id && !!currentQRData?.qrCode
  const [toBePaid, setToBePaid] = useState<Record<PayFor, number>>({} as Record<PayFor, number>)
  const [isSplit, setIsSplit] = useState(false)

  const showAppDownloadSection = client?.featureToggles?.registrationAppDownload === true

  // Get current language for App Store badge
  const currentLanguage = _getLanguageCode() || _getDefaultLanguageCode() || 'en'

  const googleMapsUrl = useMemo(
    () => client?.locations?.find((location) => location?.id === activity?.locationId)?.googleUrl,
    [client?.locations, activity?.locationId]
  )

  const onOpenGoogleMaps = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation()
      if (googleMapsUrl) {
        window.open(googleMapsUrl, '_blank')
      }
    },
    [googleMapsUrl]
  )

  const formatToBePaid = (p: PayForEnum, isTotal?: boolean): string => {
    const formatAmount = (amount: number) => (amount / 100).toFixed(2).toString().replace('.00', '')
    let amount = toBePaid[p as PayFor]
    if (isTotal === true) {
      amount =
        state.payFor === PayFor.Both
          ? toBePaid[PayFor.Both]
          : state.payFor === PayFor.Main
            ? toBePaid[PayFor.Main]
            : toBePaid[PayFor.Partner]
    }
    return formatAmount(amount)
  }

  useEffect(() => {
    if (!state.obfuscatedEnrollment.priceDetails) return

    const paymentsUsersLength = state.obfuscatedEnrollment.payments.flatMap((p) => p.userId || 'mainUserId').length
    const priceDetailsUsersLength = state.obfuscatedEnrollment.priceDetails.flatMap((pd) => pd.appliesTo).length
    let isSplitPayment = paymentsUsersLength > 1 || priceDetailsUsersLength > 1

    // case: mainUser paid for all
    if (priceDetailsUsersLength > 1 && paymentsUsersLength === 1) {
      // multiple payments required but only one payment recorded - check if paid in full
      const enrollmentPaymentsAmount = state.obfuscatedEnrollment.payments
        .filter((p) => p.type === 'pay')
        .reduce((acc, p) => acc + p.totalAmount, 0)
      const enrollmentPaymentDue = state.obfuscatedEnrollment.priceDetails.reduce((acc, pd) => acc + pd.finalPrice, 0)

      if (enrollmentPaymentsAmount === enrollmentPaymentDue) {
        isSplitPayment = false
      }
    }

    setIsSplit(isSplitPayment)
  }, [state.obfuscatedEnrollment])

  useEffect(() => {
    if (!enrollment && !state.obfuscatedEnrollment?.id) return

    // values required for payFor radio buttons
    const source = !!state.obfuscatedEnrollment?.id ? state.obfuscatedEnrollment : enrollment
    if (!source) return

    // Get toBePaid WITH admin fee included (for display purposes)
    let toBePaidData = getToBePaidData(source, true)
    setToBePaid(toBePaidData)
  }, [enrollment, state.obfuscatedEnrollment])

  const isPaid = [EnrollmentPaymentStatus.Paid, EnrollmentPaymentStatus.PartlyPaid].includes(state.paymentStatus)

  const qrSectionRef = useRef<HTMLDivElement>(null)

  // Scroll to QR/Summary section when payment is confirmed
  useEffect(() => {
    if (!isPaid) return
    const timer = setTimeout(() => {
      qrSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 300)
    return () => clearTimeout(timer)
  }, [isPaid])

  // Build Apple Wallet pass URL (works with local emulator or production)
  const appleWalletUrl = useMemo(() => {
    if (!currentQRData) return null
    const enrollmentId = state.obfuscatedEnrollment?.id || enrollment?.id
    if (!enrollmentId) return null
    return `${getFunctionUrl('appleWalletPass')}?enrollmentId=${enrollmentId}&qrCode=${encodeURIComponent(currentQRData.qrCode)}`
  }, [currentQRData, state.obfuscatedEnrollment?.id, enrollment?.id])

  // Build Google Wallet pass URL
  const googleWalletUrl = useMemo(() => {
    if (!currentQRData) return null
    const enrollmentId = state.obfuscatedEnrollment?.id || enrollment?.id
    if (!enrollmentId) return null
    return `${getFunctionUrl('googleWalletPass')}?enrollmentId=${enrollmentId}&qrCode=${encodeURIComponent(currentQRData.qrCode)}`
  }, [currentQRData, state.obfuscatedEnrollment?.id, enrollment?.id])

  return (
    <>
      <Box direction="row" gap="15px" bottom spacing={FOUR_PX}>
        {state.isRegistrationCompleted && <img src={iconCompleted} alt="Completed icon" width={24} height={24} />}
        <GrooverText textKey="registrationHeadersAndStatusHeaderFinalSummary" />
      </Box>

      <Box fullWidth direction="column" align="center" justify="center" gap={TWENTY_FOUR_PX}>
        {/* App Download Buttons - only shown for SEK clients (Swedish app) */}
        {showAppDownloadSection && currency === 'SEK' && (
          <Box fullWidth align="center" top spacing={TWENTY_FOUR_PX}>
            {isMobile ? (
              <>
                {/* Mobile: First row - Image (left) + Text (right) */}
                <Box fullWidth direction="row" align="center" gap={SIXTEEN_PX}>
                  <Box style={{ width: '30%', padding: TWELVE_PX }}>
                    <DansskolaImage
                      src="/Dansskola.png"
                      alt={getGrooverText('userWeb.registration.dansskolaImageAlt')}
                    />
                  </Box>
                  <Box
                    direction="column"
                    align="flex-start"
                    gap={EIGHT_PX}
                    style={{ width: '70%', padding: TWELVE_PX }}
                  >
                    <AppDescriptionText textKey="registrationStep5PaymentAppDescription" />
                  </Box>
                </Box>

                {/* Mobile: Second row - Download buttons centered */}
                <DownloadButtonsContainer>
                  <AppStoreDownloadButton
                    href="https://apps.apple.com/se/app/coursely-se/id6745610767?l=en-GB"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <AppleAppStoreBadge
                      language={currentLanguage}
                      src={currentLanguage === 'sv' ? swedishAppStoreBadge : englishAppStoreBadge}
                      alt={getGrooverText('userWeb.registration.appStoreBadgeAlt')}
                    />
                  </AppStoreDownloadButton>
                  <GooglePlayButton
                    href="https://play.google.com/store/apps/details?id=se.coursely.app&pcampaignid=web_share"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <GooglePlayBadge
                      src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
                      alt={getGrooverText('userWeb.registration.googlePlayBadgeAlt')}
                    />
                  </GooglePlayButton>
                </DownloadButtonsContainer>
              </>
            ) : (
              /* Web: Original layout - Image + Text + Download buttons in one row */
              <Box fullWidth direction="row" align="center" gap={THIRTY_TWO_PX}>
                <Box style={{ padding: TWENTY_FOUR_PX }}>
                  <DansskolaImage src="/Dansskola.png" alt={getGrooverText('userWeb.registration.dansskolaImageAlt')} />
                </Box>
                <Box direction="column" align="flex-start" gap={TWELVE_PX} style={{ padding: TWENTY_FOUR_PX }}>
                  <AppDescriptionText textKey="registrationStep5PaymentAppDescription" />
                  <DownloadButtonsContainer>
                    <AppStoreDownloadButton
                      href="https://apps.apple.com/se/app/coursely-se/id6745610767?l=en-GB"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <AppleAppStoreBadge
                        language={currentLanguage}
                        src={currentLanguage === 'sv' ? swedishAppStoreBadge : englishAppStoreBadge}
                        alt={getGrooverText('userWeb.registration.appStoreBadgeAlt')}
                      />
                    </AppStoreDownloadButton>
                    {/* <GooglePlayButton
                      href="https://play.google.com/store/apps/details?id=se.coursely.app&pcampaignid=web_share"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <GooglePlayBadge
                        src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
                        alt={getGrooverText('userWeb.registration.googlePlayBadgeAlt')}
                      />
                    </GooglePlayButton> */}
                  </DownloadButtonsContainer>
                </Box>
              </Box>
            )}
          </Box>
        )}

        {/* Payment Status Information */}
        {isPaid ? null : (
          <>
            {state.paymentMethod === PaymentMethod.BankTransfer && (
              <InfoContainer fullWidth align="center" justify="center" gap={EIGHT_PX}>
                <GrooverText textKey="registrationStep5PaymentWaitingBankInfo" />
                <Box
                  direction="row"
                  align="center"
                  justify="center"
                  gap={TWO_PX}
                  style={{ flexWrap: isMobile ? 'wrap' : 'nowrap' }}
                >
                  <span>
                    {formatGrooverText(getGrooverText('registrationStep5PaymentPayInfoBankAccountNewAmount'), [
                      formatToBePaid(PayFor.Both as PayForEnum, true)
                    ])}
                  </span>
                  <span>
                    {formatGrooverText(getGrooverText('registrationStep5PaymentPayInfoBankAccountNewAccountNumber'), [
                      client?.bankGiro
                    ])}
                  </span>
                  <span>
                    {formatGrooverText(getGrooverText('registrationStep5PaymentPayInfoBankAccountNewRef'), [
                      state?.obfuscatedEnrollment?.ocr ? state?.obfuscatedEnrollment?.ocr : enrollment?.ocr
                    ])}
                  </span>
                </Box>
              </InfoContainer>
            )}

            {state.paymentMethod === PaymentMethod.Cash && (
              <InfoContainer fullWidth align="center" justify="center" gap={EIGHT_PX}>
                <span>
                  {formatGrooverText(getGrooverText('registrationStep5PaymentCashInfo'), [
                    formatToBePaid(PayFor.Both as PayForEnum, true)
                  ])}
                </span>
              </InfoContainer>
            )}

            {state.paymentMethod === PaymentMethod.SwishManual && (
              <InfoContainer fullWidth align="center" justify="center" gap={EIGHT_PX}>
                <span>
                  {formatGrooverText(getGrooverText('registrationStep5PaymentSwishManualInfoExtended'), [
                    formatToBePaid(PayFor.Both as PayForEnum, true),
                    client?.swishForetagNumber
                  ])}
                </span>
              </InfoContainer>
            )}
          </>
        )}

        {/* Activity Information and QR Code - Always shown, side by side or stacked on mobile */}
        {isPaid && <GrooverText textKey="registrationStep6SummarySummaryWelcomeToCourses" />}

        {/* Wallet Buttons - Centered above content */}
        {(appleWalletUrl || googleWalletUrl) && (
          <WalletButtonsContainer
            direction="row"
            changeDirectionOnMobile
            justify="center"
            align="center"
            gap={TWELVE_PX}
            fullWidth
          >
            {/* Apple Wallet Link */}
            {appleWalletUrl && (
              <AppleWalletLink
                href={appleWalletUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Add to Apple Wallet"
              >
                <AppleWalletIcon src={appleWalletImage} alt="Add to Apple Wallet" />
              </AppleWalletLink>
            )}

            {/* Google Wallet Link */}
            {googleWalletUrl && (
              <GoogleWalletLink
                href={googleWalletUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Add to Google Wallet"
              >
                <GoogleWalletIcon src={googleWalletImage} alt="Add to Google Wallet" />
              </GoogleWalletLink>
            )}
          </WalletButtonsContainer>
        )}

        <ResponsiveContentContainer fullWidth direction={isMobile ? 'column' : 'row'} gap={TWENTY_FOUR_PX}>
          <ActivitiesContainer fullWidth gap={FOUR_PX}>
            {approvedActivities.map((activity) => (
              <ActivityDetails
                key={activity.id}
                activity={activity}
                client={client}
                expanded={expandedActivityId === activity.id}
                onExpand={() => setExpandedActivityId(activity.id === expandedActivityId ? null : activity.id)}
                onOpenGoogleMaps={onOpenGoogleMaps}
              />
            ))}
          </ActivitiesContainer>

          <div ref={qrSectionRef}>
            <QRCodeContainer fullWidth align="center" gap={TWELVE_PX} top={isMobile}>
              <GrooverText textKey="registrationStep6SummaryQrCodeLabel" />
              {allQRCodes.length > 0 && (
                <Box fullWidth direction="column" align="center" gap={SIXTEEN_PX}>
                  {/* QR Code Display */}
                  <Box fullWidth align="center" gap={TWELVE_PX}>
                    {/* Ticket Info */}
                    {currentQRData?.isMultipleTickets && (
                      <Box direction="column" align="center" gap={EIGHT_PX}>
                        <span>
                          {getGrooverText('registrationStep6SummaryQrCodeTicketLabel')} {currentQRData.ticketIndex}
                        </span>
                      </Box>
                    )}

                    {/* QR Code */}
                    <QRCodeWrapper>
                      <QRCodeElement value={currentQRData?.qrCode || ''} showText={false} />
                    </QRCodeWrapper>

                    {showOpenInCheckinApp && client?.id && currentQRData?.qrCode && (
                      <RegisterOpenInCheckinAppButton clientId={client.id} checkinQr={currentQRData.qrCode} />
                    )}
                  </Box>
                </Box>
              )}
            </QRCodeContainer>
          </div>
        </ResponsiveContentContainer>

        {/* Receipt - Only shown when paid and activity has a price */}
        {isPaid && activity.price !== 0 && (
          <>
            <CourseRegistrationReceiptWrapper
              state={state}
              activity={activity}
              onSetMainUserSsn={onSetMainUserSsn}
              onSetPartnerUserSsn={onSetPartnerUserSsn}
            />
            {isSplit && state.paymentStatus == EnrollmentPaymentStatus.PartlyPaid && (
              <WarningMessage fullWidth align="center" fullPadding spacing={SIXTEEN_PX}>
                <GrooverText textKey="registrationHeadersAndStatusHeaderStatusPartiallyPaymentOk" />
              </WarningMessage>
            )}
          </>
        )}
      </Box>
    </>
  )
}

const InfoContainer = styled(Box)`
  padding: 10px;
  border-radius: 4px;
  background-color: rgb(243, 250, 194);
`

const WarningMessage = styled(Box)`
  background: #efef19;
  border-radius: ${BorderRadixes.small};
`

const AppStoreDownloadButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  text-decoration: none;
  transition: opacity 0.2s ease;
  border: none;
  background: none;

  &:hover {
    opacity: 0.8;
  }

  &:active {
    opacity: 0.6;
  }
`

const AppleAppStoreBadge = styled.img<{ language: 'en' | 'sv' | 'no' }>`
  width: 180px;
  height: 60px;

  @media (max-width: 1024px) {
    width: 150px;
    height: 50px;
  }

  @media (max-width: 768px) {
    width: 140px;
    height: 47px;
  }
`

const DownloadButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  margin-top: 16px;

  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: center;
    gap: 20px;
  }
`

const GooglePlayButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  text-decoration: none;
  transition: opacity 0.2s ease;
  border: none;
  background: none;

  &:hover {
    opacity: 0.8;
  }

  &:active {
    opacity: 0.6;
  }
`

const GooglePlayBadge = styled.img`
  width: 180px;
  height: 60px;

  @media (max-width: 1024px) {
    width: 150px;
    height: 50px;
  }

  @media (max-width: 768px) {
    width: 140px;
    height: 47px;
  }
`

const DansskolaImage = styled.img`
  width: 200px;
  height: auto;
  flex-shrink: 0;

  @media (max-width: 1024px) {
    width: 150px;
  }

  @media (max-width: 768px) {
    width: 120px;
  }
`

const AppDescriptionText = styled(GrooverText)`
  font-size: 18px;
  line-height: 1.4;
  max-width: 400px;

  @media (max-width: 1024px) {
    font-size: 16px;
    max-width: 300px;
  }

  @media (max-width: 768px) {
    font-size: 14px;
  }
`

const ResponsiveContentContainer = styled(Box)`
  @media (max-width: 1024px) {
    flex-direction: column !important;
    gap: ${SIXTEEN_PX} !important;
  }
`

const ActivitiesContainer = styled(Box)`
  @media (max-width: 1024px) {
    max-width: 100%;
  }
`

const QRCodeContainer = styled(Box)`
  @media (max-width: 1024px) {
    margin-top: ${TWELVE_PX};
    align-items: center;
  }

  @media (min-width: 1025px) {
    min-width: 400px;
    max-width: 400px;
  }
`

const QRCodeWrapper = styled.div`
  max-width: 300px;
  width: 100%;
  display: flex;
  justify-content: center;

  @media (max-width: 1024px) {
    max-width: 250px;
  }

  @media (max-width: 768px) {
    max-width: 200px;
  }

  & > div {
    max-width: 100%;
    height: auto;
  }
`

const AppleWalletLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  text-decoration: none;
  transition: opacity 0.2s ease;
  border: none;
  background: none;

  &:hover {
    opacity: 0.8;
  }

  &:active {
    opacity: 0.6;
  }
`

const AppleWalletIcon = styled.img`
  width: 180px;
  height: auto;

  @media (min-width: 1024px) {
    width: 220px;
  }

  @media (max-width: 768px) {
    width: 160px;
  }

  @media (max-width: 480px) {
    width: 140px;
  }
`

const WalletButtonsContainer = styled(Box)`
  margin-bottom: ${SIXTEEN_PX};
  flex-wrap: wrap;
`

const GoogleWalletLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  text-decoration: none;
  transition: opacity 0.2s ease;
  border: none;
  background: none;

  &:hover {
    opacity: 0.8;
  }

  &:active {
    opacity: 0.6;
  }
`

const GoogleWalletIcon = styled.img`
  width: 180px;
  height: auto;

  @media (min-width: 1024px) {
    width: 220px;
  }

  @media (max-width: 768px) {
    width: 160px;
  }

  @media (max-width: 480px) {
    width: 140px;
  }
`

export default memo(CourseRegistrationStepFiveOpenedContent)
